/**
 * This function checks if the layer ID matches a pattern for a given utility's reference layer type.
 * @param {string} layerId - the ID of the layer to check
 * @param {string} layerType - the type of layer to check for
 * @returns {boolean} - true if the layer ID matches the pattern for the given utility's reference layer type, false otherwise
 */
export function LayerIdIsUtilityRefLayerType(layerId, layerType) {
  let utilityCompany = globalThis.config.firebaseData.utilityCompany;

  // Re-map 'ppl_attachments' to 'ppl' for for legacy utility data structure
  if (utilityCompany == 'ppl_attachments') utilityCompany = 'ppl';

  switch (layerType) {
    case 'poles':
      return layerId?.includes(`${utilityCompany}--poles`) || layerId?.includes(`${utilityCompany}--pole_attributes_imports`);
    case 'primary':
      // For LG&E, the primary layer is named `--primary` instead of `--primary_overhead` and they don't have a `--primary_underground` layer
      return (
        layerId?.includes(`${utilityCompany}--primary_overhead`) ||
        layerId?.includes(`${utilityCompany}--primary_overhead_imports`) ||
        (utilityCompany == 'lge' && layerId?.includes(`${utilityCompany}--primary`))
      );
    case 'primary-underground':
      return (
        layerId?.includes(`${utilityCompany}--primary_underground`) || layerId?.includes(`${utilityCompany}--primary_underground_imports`)
      );
    case 'secondary':
      // For LG&E, the secondary layer is named `--secondary` instead of `--secondadry_overhead` and they don't have a `--secondadry_underground` layer
      return (
        layerId?.includes(`${utilityCompany}--secondary_overhead`) ||
        layerId?.includes(`${utilityCompany}--secondary_overhead_imports`) ||
        (utilityCompany == 'lge' && layerId?.includes(`${utilityCompany}--secondary`))
      );
    case 'secondary-underground':
      return (
        layerId?.includes(`${utilityCompany}--secondary_underground`) ||
        layerId?.includes(`${utilityCompany}--secondary_underground_imports`)
      );
    case 'roads':
      return layerId?.includes(`${utilityCompany}--state_roads`) || layerId?.includes(`${utilityCompany}--state_roads_imports`);
    case 'span-guy':
      return layerId?.includes(`${utilityCompany}--span_guy`) || layerId?.includes(`${utilityCompany}--span_guy_imports`);
    case 'current-limiting-fuse':
      return (
        layerId?.includes(`${utilityCompany}--current_limiting_fuse`) ||
        layerId?.includes(`${utilityCompany}--current_limiting_fuse_imports`)
      );
    case 'airports':
      return layerId?.includes(`${utilityCompany}--airports`);
    default:
      return false;
  }
}

/**
 * This function checks if the layer ID is a utility data layer.
 * @param {string} layerId - the ID of the layer to check
 * @returns {boolean} - true if the layer is a utility data layer
 */
export function LayerIdIsUtilityLayer(layerId) {
  return (
    LayerIdIsUtilityRefLayerType(layerId, 'poles') ||
    LayerIdIsUtilityRefLayerType(layerId, 'primary') ||
    LayerIdIsUtilityRefLayerType(layerId, 'primary-underground') ||
    LayerIdIsUtilityRefLayerType(layerId, 'secondary') ||
    LayerIdIsUtilityRefLayerType(layerId, 'secondary-underground') ||
    LayerIdIsUtilityRefLayerType(layerId, 'roads') ||
    LayerIdIsUtilityRefLayerType(layerId, 'span-guy') ||
    LayerIdIsUtilityRefLayerType(layerId, 'current-limiting-fuse') ||
    LayerIdIsUtilityRefLayerType(layerId, 'airports')
  );
}

/**
 * Normalizes utility_info/_list during the gradual rollout from array to object storage.
 * Returns stable array items with their backing RTDB key preserved on $key.
 *
 * @param {Array|Object|null|undefined} utilityInfoList
 * @returns {Array<Object & { $key: string }>}
 */
export function GetUtilityInfoListEntries(utilityInfoList) {
  // Turn the input into a uniform list of [key, layer] pairs so the rest of the
  // function can treat arrays and objects the same way.
  // - For an array, the key is the array index (e.g. "0", "1", ...).
  // - For an object, the key is the RTDB child key (e.g. "layer_a").
  // - A null/undefined input is treated as an empty object.
  const keyedLayerPairs = Array.isArray(utilityInfoList)
    ? utilityInfoList.map((layer, index) => [String(index), layer])
    : Object.entries(utilityInfoList ?? {});

  return (
    keyedLayerPairs
      // Attach the backing key as $key (so callers can find the layer in the DB)
      // and keep the original position as a sort fallback. Non-objects (e.g. null
      // gaps in the array) become null so we can drop them next.
      .map(([key, layer], originalIndex) => {
        if (layer == null || typeof layer !== 'object') return null;
        return { $key: key, ...layer, originalIndex };
      })
      .filter((layer) => layer != null)
      .sort((layerA, layerB) => {
        const sortOrderA = layerA.sort_order ?? layerA.originalIndex;
        const sortOrderB = layerB.sort_order ?? layerB.originalIndex;
        return sortOrderA - sortOrderB;
      })
      // Drop the temporary originalIndex helper, keeping everything else.
      .map(({ originalIndex, ...layer }) => layer)
  );
}

/**
 * Checks whether the user's company has access to a specific utility layer.
 * @param {object} layer - the utility layer object to check
 * @param {string} utilityCompany - the utility company who owns the layer
 * @param {string} userGroup - the user's company ID
 * @param {boolean} isUtilityReviewContractor - whether the user's company is a utility review contractor
 * @returns {boolean} - true if the utility company has access to the layer, false otherwise
 */
export function CompanyHasAccessToUtilityLayer(layer, utilityCompany, userGroup, isUtilityReviewContractor) {
  if (!layer || !userGroup) return false;

  // Utility reference layers have type 'Reference Layer'
  if (layer.type != 'Reference Layer') return true;

  if (layer.options?.firestore === true) return true;

  const hasUtilityAccess = () => {
    return userGroup == utilityCompany || Boolean(isUtilityReviewContractor);
  };

  // Check the layer's url to see if the company has access to it
  const urlParts = (layer.url ?? '').split('/');
  const layerBelongsToCompany = urlParts[0] == 'companies' && urlParts[1] == userGroup;
  const isUtilityLayer = LayerIdIsUtilityLayer(layer.$key);
  const hasExplicitLayerAccess = layer.map_layer_access?.[userGroup] === true;
  return layerBelongsToCompany || (isUtilityLayer && (hasUtilityAccess() || hasExplicitLayerAccess));
}
